<script setup lang="ts">
  import { computed } from 'vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import { getReservationSetupSteps } from '@/domain/reservations/reservation-setup';
  import type { IReservationSetupStep } from '@/domain/reservations/reservation-setup';
  import type { IReservationSettingsConfiguration } from '@/interfaces/IReservationSettings';

  const props = defineProps<{
    configuration: IReservationSettingsConfiguration;
  }>();
  const emit = defineEmits<{
    (event: 'go', section: IReservationSetupStep['section']): void;
    /** Open the step-by-step guide. */
    (event: 'setup'): void;
  }>();
  const setup = computed(() => getReservationSetupSteps(props.configuration));
  const requiredSteps = computed(() =>
    setup.value.steps.filter((step) => !step.optional)
  );
  const done = computed(
    () => requiredSteps.value.filter((step) => step.done).length
  );
  const hintKeys: Record<IReservationSetupStep['id'], string> = {
    enabled: 'reservations.setup.enabledHint',
    shifts: 'reservations.shifts.description',
    tables: 'reservations.setup.tablesHint',
    coverage: 'reservations.setup.coverageHint',
    online: 'reservations.setup.onlineHint',
    alerts: 'reservations.setup.alertsHint',
  };
  // Seats exist but none of those tables is online: a different fix.
  function nothingOnline(step: IReservationSetupStep): boolean {
    return step.id === 'coverage' && step.params?.bookable === 0;
  }
  function titleKey(step: IReservationSetupStep): string {
    return nothingOnline(step)
      ? 'reservations.setup.coverageNone'
      : `reservations.setup.${step.id}`;
  }
  function hintKey(step: IReservationSetupStep): string {
    if (step.blocked) return 'reservations.setup.onlineBlockedHint';
    if (nothingOnline(step)) return 'reservations.setup.coverageNoneHint';
    return hintKeys[step.id];
  }
</script>

<template>
  <section class="reservation-setup-checklist">
    <header class="setup-header">
      <h2>{{ $t('reservations.setup.title') }}</h2>
      <p class="progress-label">
        {{
          $t('reservations.setup.progress', {
            done,
            total: requiredSteps.length,
          })
        }}
      </p>
    </header>
    <progress
      class="setup-progress"
      :value="done"
      :max="requiredSteps.length"
      :aria-label="$t('reservations.setup.title')"
    />
    <!-- Soft: the save bar's solid button stays the only solid CTA on screen. -->
    <PosUiButton
      class="setup-cta"
      color="primary"
      variant="soft"
      icon="compass-outline"
      @click="emit('setup')"
    >
      {{ $t('reservations.guide.open') }}
    </PosUiButton>
    <ul class="setup-steps">
      <li
        v-for="step in setup.steps"
        :key="step.id"
        class="setup-step"
        :class="{ blocked: step.blocked }"
      >
        <span
          class="step-dot"
          :class="{ done: step.done }"
          aria-hidden="true"
        />
        <div class="step-content">
          <div class="step-heading">
            <h3>{{ $t(titleKey(step)) }}</h3>
            <PosUiBadge v-if="step.optional" color="neutral">
              {{ $t('reservations.setup.optional') }}
            </PosUiBadge>
          </div>
          <p>{{ $t(hintKey(step), step.params) }}</p>
        </div>
        <PosUiButton
          v-if="!step.done && !step.blocked"
          class="step-action"
          color="neutral"
          variant="outline"
          @click="emit('go', step.section)"
        >
          {{ $t('reservations.setup.go') }}
        </PosUiButton>
      </li>
    </ul>
  </section>
</template>

<style scoped lang="scss">
  .reservation-setup-checklist {
    @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-5 tw-py-4 md:tw-px-6;

    .setup-header {
      @apply tw-flex tw-flex-wrap tw-items-baseline tw-justify-between tw-gap-2;

      h2 {
        @apply tw-m-0 tw-text-base tw-font-semibold tw-text-neutral-900;
      }
    }

    .progress-label {
      @apply tw-m-0 tw-text-sm tw-tabular-nums tw-text-neutral-500;
    }

    .setup-progress {
      @apply tw-mt-3 tw-block tw-h-1.5 tw-w-full tw-overflow-hidden tw-appearance-none tw-rounded-full tw-border-0 tw-bg-neutral-100;
      color: theme('colors.emerald.500');

      &::-webkit-progress-bar {
        @apply tw-rounded-full tw-bg-neutral-100;
      }

      &::-webkit-progress-value {
        @apply tw-rounded-full tw-bg-emerald-500;
      }

      &::-moz-progress-bar {
        @apply tw-rounded-full tw-bg-emerald-500;
      }
    }

    .setup-cta {
      @apply tw-mt-4;
    }

    .setup-steps {
      @apply tw-mb-0 tw-mt-2 tw-list-none tw-p-0;
    }

    .setup-step {
      @apply tw-grid tw-grid-cols-[auto_minmax(0,1fr)] tw-items-start tw-gap-x-3 tw-gap-y-2 tw-py-3;

      &.blocked {
        @apply tw-opacity-60;
      }

      & + .setup-step {
        @apply tw-border-0 tw-border-t tw-border-solid tw-border-neutral-200;
      }
    }

    .step-dot {
      @apply tw-mt-1.5 tw-h-2 tw-w-2 tw-rounded-full tw-bg-neutral-300;

      &.done {
        @apply tw-bg-emerald-500;
      }
    }

    .step-content {
      @apply tw-min-w-0;

      p {
        @apply tw-mb-0 tw-mt-1 tw-text-xs tw-leading-relaxed tw-text-neutral-500;
      }
    }

    .step-heading {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2;

      h3 {
        @apply tw-m-0 tw-text-sm tw-font-medium tw-text-neutral-900;
      }
    }

    .step-action {
      @apply tw-col-start-2 tw-justify-self-start;
    }

    @media (min-width: 640px) {
      .setup-step {
        @apply tw-grid-cols-[auto_minmax(0,1fr)_auto];
      }

      .step-action {
        @apply tw-col-start-3 tw-row-start-1 tw-self-center;
      }
    }
  }
</style>
